// ===========================================
// NoteContent
// ===========================================
// The body of a note card, with a "Show more" toggle that appears ONLY when the
// text is genuinely truncated.
//
// One component rather than four hand-copied clamp blocks: the four note cards
// each carried their own copy of the clamp `sx`, and the Part A review
// (md/review/notes-relation-mapping-2026-09-15.md) flagged exactly that
// duplication — four independently reimplemented guards, of which a future edit
// would "simplify" one and silently regress it.
//
// `showFullContent` (used by the all-notes dialogs) opts out of clamping
// entirely, since those surfaces exist to show everything.

import { Box, Link, Typography } from '@mui/material';
import { useEffect, useId, useState } from 'react';
import { useClampedOverflow } from './useClampedOverflow';

/** Lines a collapsed note is clamped to. */
const CLAMP_LINES = 2;

interface Props {
  content: string;
  /** Render un-clamped with no toggle (the all-notes dialogs). */
  showFullContent?: boolean;
}

export default function NoteContent({ content, showFullContent = false }: Props) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { ref, isOverflowing } = useClampedOverflow(content, isExpanded);

  // Collapse when the body changes underneath us. Every list that renders these
  // cards keys on the note id, so a changed LIST remounts — but an in-place
  // edit of one note keeps the same id with new text, and without this the card
  // stays expanded from the previous body, showing "Show less" over content
  // that was never measured.
  useEffect(() => {
    setIsExpanded(false);
  }, [content]);

  const isClamped = !showFullContent && !isExpanded;
  const bodyId = useId();

  return (
    <Box>
      <Typography
        id={bodyId}
        ref={ref}
        variant="body2"
        color="text.secondary"
        sx={{
          // Teachers write multi-line notes; without pre-wrap the line breaks
          // collapse and the note reads as one run-on paragraph.
          whiteSpace: 'pre-wrap',
          ...(isClamped
            ? {
                display: '-webkit-box',
                WebkitLineClamp: CLAMP_LINES,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }
            : {}),
        }}
      >
        {content}
      </Typography>

      {!showFullContent && (isOverflowing || isExpanded) && (
        <Link
          component="button"
          type="button"
          variant="caption"
          underline="hover"
          // The label already changes between "Show more"/"Show less", but
          // aria-expanded is the conventional annotation and tells a screen
          // reader WHICH region the control governs.
          aria-expanded={isExpanded}
          aria-controls={bodyId}
          onClick={() => setIsExpanded((v) => !v)}
          sx={{ mt: 0.5, display: 'block' }}
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </Link>
      )}
    </Box>
  );
}
